import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  ContentCopy,
  Download,
  Edit,
  Print,
  Refresh,
  Save,
  Cancel,
  History,
} from '@mui/icons-material';
import Markdown from 'markdown-to-jsx';
import { marked } from 'marked';
import html2pdf from 'html2pdf.js';
import moment from 'moment';
import { useSoapNotes } from '../../contexts/api-contexts/SoapNotesContext';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { useAnimalTypes } from '../../contexts/api-contexts/AnimalTypeContext';
import { useBreeds } from '../../contexts/api-contexts/BreedsContext';
import { SoapNote } from '../../types/SoapNote.type';

// Configure marked for GitHub Flavored Markdown
marked.setOptions({
  gfm: true,
  breaks: true,
});

// Language options for SOAP notes generation (same as ambient listening)
const soapLanguages = [
  { code: 'ab-GE', name: 'Abkhaz' },
  { code: 'af-ZA', name: 'Afrikaans' },
  { code: 'ar-AE', name: 'Arabic, Gulf' },
  { code: 'ar-SA', name: 'Arabic, Modern Standard' },
  { code: 'hy-AM', name: 'Armenian' },
  { code: 'ast-ES', name: 'Asturian' },
  { code: 'az-AZ', name: 'Azerbaijani' },
  { code: 'ba-RU', name: 'Bashkir' },
  { code: 'eu-ES', name: 'Basque' },
  { code: 'be-BY', name: 'Belarusian' },
  { code: 'bn-IN', name: 'Bengali' },
  { code: 'bs-BA', name: 'Bosnian' },
  { code: 'bg-BG', name: 'Bulgarian' },
  { code: 'ca-ES', name: 'Catalan' },
  { code: 'ckb-IR', name: 'Central Kurdish, Iran' },
  { code: 'ckb-IQ', name: 'Central Kurdish, Iraq' },
  { code: 'zh-HK', name: 'Chinese (Cantonese), Hong-Kong' },
  { code: 'zh-CN', name: 'Chinese, Simplified' },
  { code: 'zh-TW', name: 'Chinese, Traditional' },
  { code: 'hr-HR', name: 'Croatian' },
  { code: 'cs-CZ', name: 'Czech' },
  { code: 'da-DK', name: 'Danish' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'en-AU', name: 'English, Australian' },
  { code: 'en-GB', name: 'English, British' },
  { code: 'en-IN', name: 'English, Indian' },
  { code: 'en-IE', name: 'English, Irish' },
  { code: 'en-NZ', name: 'English, New Zealand' },
  { code: 'en-AB', name: 'English, Scottish' },
  { code: 'en-ZA', name: 'English, South African' },
  { code: 'en-US', name: 'English, US' },
  { code: 'en-WL', name: 'English, Welsh' },
  { code: 'et-ET', name: 'Estonian' },
  { code: 'fa-IR', name: 'Farsi' },
  { code: 'fi-FI', name: 'Finnish' },
  { code: 'fr-FR', name: 'French' },
  { code: 'fr-CA', name: 'French, Canadian' },
  { code: 'gl-ES', name: 'Galician' },
  { code: 'ka-GE', name: 'Georgian' },
  { code: 'de-DE', name: 'German' },
  { code: 'de-CH', name: 'German, Swiss' },
  { code: 'el-GR', name: 'Greek' },
  { code: 'gu-IN', name: 'Gujarati' },
  { code: 'ha-NG', name: 'Hausa' },
  { code: 'he-IL', name: 'Hebrew' },
  { code: 'hi-IN', name: 'Hindi, Indian' },
  { code: 'hu-HU', name: 'Hungarian' },
  { code: 'is-IS', name: 'Icelandic' },
  { code: 'id-ID', name: 'Indonesian' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'kab-DZ', name: 'Kabyle' },
  { code: 'kn-IN', name: 'Kannada' },
  { code: 'kk-KZ', name: 'Kazakh' },
  { code: 'rw-RW', name: 'Kinyarwanda' },
  { code: 'ko-KR', name: 'Korean' },
  { code: 'ky-KG', name: 'Kyrgyz' },
  { code: 'lv-LV', name: 'Latvian' },
  { code: 'lt-LT', name: 'Lithuanian' },
  { code: 'lg-IN', name: 'Luganda' },
  { code: 'mk-MK', name: 'Macedonian' },
  { code: 'ms-MY', name: 'Malay' },
  { code: 'ml-IN', name: 'Malayalam' },
  { code: 'mt-MT', name: 'Maltese' },
  { code: 'mr-IN', name: 'Marathi' },
  { code: 'mhr-RU', name: 'Meadow Mari' },
  { code: 'mn-MN', name: 'Mongolian' },
  { code: 'no-NO', name: 'Norwegian Bokmål' },
  { code: 'or-IN', name: 'Odia/Oriya' },
  { code: 'ps-AF', name: 'Pashto' },
  { code: 'pl-PL', name: 'Polish' },
  { code: 'pt-PT', name: 'Portuguese' },
  { code: 'pt-BR', name: 'Portuguese, Brazilian' },
  { code: 'pa-IN', name: 'Punjabi' },
  { code: 'ro-RO', name: 'Romanian' },
  { code: 'ru-RU', name: 'Russian' },
  { code: 'sr-RS', name: 'Serbian' },
  { code: 'si-LK', name: 'Sinhala' },
  { code: 'sk-SK', name: 'Slovak' },
  { code: 'sl-SI', name: 'Slovenian' },
  { code: 'so-SO', name: 'Somali' },
  { code: 'es-ES', name: 'Spanish' },
  { code: 'es-US', name: 'Spanish, US' },
  { code: 'su-ID', name: 'Sundanese' },
  { code: 'sw-KE', name: 'Swahili, Kenya' },
  { code: 'sw-BI', name: 'Swahili, Burundi' },
  { code: 'sw-RW', name: 'Swahili, Rwanda' },
  { code: 'sw-TZ', name: 'Swahili, Tanzania' },
  { code: 'sw-UG', name: 'Swahili, Uganda' },
  { code: 'sv-SE', name: 'Swedish' },
  { code: 'tl-PH', name: 'Tagalog/Filipino' },
  { code: 'ta-IN', name: 'Tamil' },
  { code: 'tt-RU', name: 'Tatar' },
  { code: 'te-IN', name: 'Telugu' },
  { code: 'th-TH', name: 'Thai' },
  { code: 'tr-TR', name: 'Turkish' },
  { code: 'uk-UA', name: 'Ukrainian' },
  { code: 'ug-CN', name: 'Uyghur' },
  { code: 'uz-UZ', name: 'Uzbek' },
  { code: 'vi-VN', name: 'Vietnamese' },
  { code: 'cy-WL', name: 'Welsh' },
  { code: 'wo-SN', name: 'Wolof' },
  { code: 'zu-ZA', name: 'Zulu' },
];

export interface SoapNotesDialogProps
  extends Omit<DialogProps, 'onClose' | 'open'> {
  open: boolean;
  onClose: () => void;
  caseId?: string;
}

export const SoapNotesDialog: React.FC<SoapNotesDialogProps> = ({
  open,
  onClose,
  caseId: propCaseId,
  ...rest
}) => {
  const {
    soapNotes,
    activeSoapNote,
    isLoading,
    isGenerating,
    error,
    fetchSoapNotes,
    generateSoapNote,
    updateSoapNote,
    setActiveSoapNote,
  } = useSoapNotes();

  const { currentCase } = useCases();
  const { animalTypes } = useAnimalTypes();
  const { breeds } = useBreeds();

  const caseId = propCaseId || currentCase?.id;

  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [copySuccess, setCopySuccess] = useState<boolean | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    return localStorage.getItem('gekko/transcribe-language') || 'en-US';
  });

  // Get patient info for display
  const patientInfo = useMemo(() => {
    if (!currentCase?.patient) return { name: 'Unknown Patient' };
    const patient = currentCase.patient;
    const animalType = animalTypes.find(
      (at) => at.id === patient.animal_type_id
    );
    const breedList = breeds[patient.animal_type_id] || [];
    const breed = breedList.find((b) => b.id === patient.breed_id);

    return {
      name: patient.name || 'Unknown Patient',
      animalType: animalType?.name || 'Unknown',
      breed: breed?.name || 'Unknown',
      weight: patient.weight_kg,
    };
  }, [currentCase?.patient, animalTypes, breeds]);

  // Fetch SOAP notes when dialog opens
  useEffect(() => {
    if (open && caseId) {
      fetchSoapNotes(caseId).catch(console.error);
    }
  }, [open, caseId, fetchSoapNotes]);

  // Initialize edited content when active note changes
  useEffect(() => {
    if (activeSoapNote && !isEditing) {
      setEditedContent(activeSoapNote.content);
    }
  }, [activeSoapNote, isEditing]);

  // Auto-generate if no notes exist
  useEffect(() => {
    if (
      open &&
      caseId &&
      !isLoading &&
      !isGenerating &&
      soapNotes.length === 0 &&
      !error
    ) {
      generateSoapNote(caseId, { language: selectedLanguage }).catch(
        console.error
      );
    }
  }, [
    open,
    caseId,
    isLoading,
    isGenerating,
    soapNotes.length,
    error,
    generateSoapNote,
    selectedLanguage,
  ]);

  const handleGenerateNew = useCallback(() => {
    if (caseId) {
      generateSoapNote(caseId, { language: selectedLanguage }).catch(
        console.error
      );
    }
  }, [caseId, generateSoapNote, selectedLanguage]);

  const handleEditClick = () => {
    if (activeSoapNote) {
      setEditedContent(activeSoapNote.content);
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async () => {
    if (caseId && activeSoapNote) {
      try {
        await updateSoapNote(caseId, activeSoapNote.id, {
          content: editedContent,
        });
        setIsEditing(false);
      } catch (err) {
        console.error('Failed to save SOAP note:', err);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditedContent(activeSoapNote?.content || '');
    setIsEditing(false);
  };

  const handleLanguageChange = (languageCode: string) => {
    setSelectedLanguage(languageCode);
    localStorage.setItem('gekko/transcribe-language', languageCode);
  };

  const handleSelectVersion = (note: SoapNote) => {
    setActiveSoapNote(note);
    setShowHistory(false);
  };

  const getTimeAgo = (date: string) => {
    return moment(date).fromNow();
  };

  const downloadPdf = () => {
    if (!activeSoapNote) return;

    const cleanedContent = activeSoapNote.content
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .replace(/^\s+|\s+$/g, '')
      .replace(/[ \t]+$/gm, '');

    const htmlContent = marked.parse(cleanedContent);

    const container = document.createElement('div');
    container.innerHTML = `
      <div class="pdf-container">
        <h1>SOAP Notes - ${patientInfo.name}</h1>
        <p>Date: ${new Date(
          activeSoapNote.generatedAt
        ).toLocaleDateString()}</p>
        <div class="content">${htmlContent}</div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      .pdf-container {
        font-family: Arial, sans-serif;
        font-size: 12px;
        line-height: 1.4;
        color: #333;
        max-width: 100%;
        word-wrap: break-word;
      }
      .pdf-container h1 { font-size: 18px; margin-bottom: 10px; }
      .pdf-container h2 { font-size: 16px; margin-top: 15px; margin-bottom: 8px; }
      .pdf-container h3 { font-size: 14px; margin-top: 12px; margin-bottom: 6px; }
      .pdf-container p { margin-bottom: 8px; }
      .pdf-container ul, .pdf-container ol { margin: 8px 0 12px 0; padding-left: 30px; }
      .pdf-container li { margin-bottom: 4px; line-height: 1.5; }
      .pdf-container table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
      .pdf-container th, .pdf-container td { border: 1px solid #ddd; padding: 6px; text-align: left; }
    `;
    container.appendChild(style);

    const opt = {
      margin: [15, 15, 15, 15],
      filename: `SOAP_Notes_${patientInfo.name}_${
        new Date().toISOString().split('T')[0]
      }.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
    };

    html2pdf().set(opt).from(container).save();
  };

  const printNotes = () => {
    if (!activeSoapNote) return;

    const cleanedContent = activeSoapNote.content
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .replace(/^\s+|\s+$/g, '')
      .replace(/[ \t]+$/gm, '');

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>SOAP Notes - ${patientInfo.name}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.4; color: #333; }
              .container { max-width: 800px; margin: 0 auto; }
              h1 { color: #333; }
              h2, h3 { margin-top: 15px; margin-bottom: 8px; }
              p { margin-bottom: 8px; }
              ul, ol { margin: 8px 0 12px 0; padding-left: 30px; }
              li { margin-bottom: 4px; line-height: 1.5; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
              th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
              .no-print { margin-bottom: 20px; padding: 10px; background: #f5f5f5; }
              @media print { .no-print { display: none; } }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="no-print">
                <button onclick="window.print()">Print</button>
                <button onclick="window.close()">Close</button>
              </div>
              <h1>SOAP Notes - ${patientInfo.name}</h1>
              <p>Date: ${new Date(
                activeSoapNote.generatedAt
              ).toLocaleDateString()}</p>
              ${marked.parse(cleanedContent)}
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() { window.print(); }, 500);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const copyToClipboard = async () => {
    if (!activeSoapNote) return;

    try {
      const htmlContent = marked.parse(activeSoapNote.content);
      const wordCompatibleHtml = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office">
        <head><meta charset="utf-8"><title>SOAP Notes</title></head>
        <body>${htmlContent}</body>
        </html>
      `;

      const blob = new Blob([wordCompatibleHtml], { type: 'text/html' });
      const item = new ClipboardItem({
        'text/html': blob,
        'text/plain': new Blob([activeSoapNote.content], {
          type: 'text/plain',
        }),
      });

      await navigator.clipboard.write([item]);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      try {
        await navigator.clipboard.writeText(activeSoapNote.content);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(null), 2000);
      } catch (fallbackErr) {
        setCopySuccess(false);
        setTimeout(() => setCopySuccess(null), 2000);
      }
    }
  };

  const renderContent = () => {
    if (showHistory) {
      return (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            SOAP Notes History
          </Typography>
          <List>
            {soapNotes.map((note, index) => (
              <React.Fragment key={note.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={activeSoapNote?.id === note.id}
                    onClick={() => handleSelectVersion(note)}
                  >
                    <ListItemText
                      primary={`Version ${note.version}`}
                      secondary={`Generated ${getTimeAgo(
                        note.generatedAt
                      )} - ${note.language.toUpperCase()}`}
                    />
                    {index === 0 && (
                      <Chip label="Latest" size="small" color="primary" />
                    )}
                  </ListItemButton>
                </ListItem>
                {index < soapNotes.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
          <Button
            variant="outlined"
            onClick={() => setShowHistory(false)}
            sx={{ mt: 2 }}
          >
            Back to Notes
          </Button>
        </Box>
      );
    }

    if (isGenerating) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Generating SOAP notes...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (isEditing) {
      return (
        <Box>
          <TextField
            multiline
            fullWidth
            rows={20}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            variant="outlined"
            placeholder="Edit your SOAP notes in Markdown format..."
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '14px',
              },
            }}
          />
          <Box
            sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}
          >
            <Button
              onClick={handleCancelEdit}
              variant="outlined"
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              startIcon={<Save />}
              disabled={isLoading}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      );
    }

    if (activeSoapNote) {
      return (
        <Box>
          <Markdown>{activeSoapNote.content}</Markdown>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="textSecondary">
          No SOAP notes available. Click &quot;Generate&quot; to create one.
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth {...rest}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          SOAP Notes
          {activeSoapNote && !showHistory && (
            <Chip
              label={`v${activeSoapNote.version}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="soap-language-select-label">Language</InputLabel>
            <Select
              labelId="soap-language-select-label"
              value={selectedLanguage}
              label="Language"
              onChange={(e) => handleLanguageChange(e.target.value)}
              disabled={isGenerating || isEditing}
            >
              {soapLanguages.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {soapNotes.length > 1 && !showHistory && (
            <Tooltip title="View history">
              <IconButton onClick={() => setShowHistory(true)} size="small">
                <History />
              </IconButton>
            </Tooltip>
          )}
          {!isGenerating && activeSoapNote && !showHistory && (
            <IconButton
              onClick={isEditing ? handleCancelEdit : handleEditClick}
              color={isEditing ? 'default' : 'primary'}
              size="small"
            >
              {isEditing ? <Cancel /> : <Edit />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>AI-Generated Content:</strong> This content was generated by
          artificial intelligence. Please review and verify all information
          before using in clinical practice.
        </Alert>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        {!isEditing && !showHistory && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Button
                onClick={handleGenerateNew}
                disabled={isGenerating || !caseId}
                variant="contained"
                startIcon={
                  isGenerating ? <CircularProgress size={20} /> : <Refresh />
                }
              >
                Generate New
              </Button>
              {activeSoapNote && (
                <Typography
                  variant="caption"
                  sx={{ ml: 1, color: 'text.secondary' }}
                >
                  Last updated {getTimeAgo(activeSoapNote.updatedAt)}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Download as PDF">
                <Button
                  onClick={downloadPdf}
                  variant="outlined"
                  disabled={isGenerating || !activeSoapNote}
                  startIcon={<Download />}
                >
                  PDF
                </Button>
              </Tooltip>
              <Tooltip
                title={copySuccess === true ? 'Copied!' : 'Copy to clipboard'}
              >
                <Button
                  onClick={copyToClipboard}
                  variant="outlined"
                  disabled={isGenerating || !activeSoapNote}
                  color={copySuccess === true ? 'success' : 'primary'}
                  startIcon={<ContentCopy />}
                >
                  {copySuccess === true ? 'Copied!' : 'Copy'}
                </Button>
              </Tooltip>
              <Tooltip title="Print notes">
                <Button
                  onClick={printNotes}
                  variant="outlined"
                  disabled={isGenerating || !activeSoapNote}
                  startIcon={<Print />}
                >
                  Print
                </Button>
              </Tooltip>
            </Stack>
          </>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SoapNotesDialog;
